// ─── Notation du moniteur après une leçon (calculs purs) ───────────────────────────────────────

export type RatingCriterion = { key: string; label: string };

export type LessonToRate = {
  lessonId: string;
  startsAt: string;
  endsAt: string;
  instructorName: string | null;
  /** Fin du délai pour noter (14 jours après la leçon). */
  deadline: string;
};

export type PendingRatings = { criteria: RatingCriterion[]; lessons: LessonToRate[] };

export type RatingDraft = { score: number | null; criteria: Record<string, number>; comment: string };

export const RATING_COMMENT_MAX = 500;

export const emptyRating = (): RatingDraft => ({ score: null, criteria: {}, comment: '' });

/** Leçons à noter, ou aucune (réponse inattendue) : rien n'est proposé. */
export function pendingRatingsOf(data: unknown): PendingRatings {
  const empty: PendingRatings = { criteria: [], lessons: [] };
  if (!data || typeof data !== 'object') return empty;
  const { criteria, lessons } = data as Partial<PendingRatings>;
  if (!Array.isArray(lessons)) return empty;
  return {
    criteria: Array.isArray(criteria) ? criteria : [],
    lessons: lessons.filter((l) => l && typeof l.lessonId === 'string'),
  };
}

/** Note de 0,5 à 5 par demi-étoile. */
export function isStarScore(value: unknown): value is number {
  return typeof value === 'number' && value >= 0.5 && value <= 5 && Number.isInteger(value * 2);
}

/** Note désignée sur une étoile (1 à 5) : moitié gauche = demi-étoile. */
export function starScore(star: number, leftHalf: boolean): number {
  return leftHalf ? star - 0.5 : star;
}

/** Note suivante au clavier (flèches), bornée de 0,5 à 5. */
export function stepScore(current: number | null, delta: number): number {
  const next = (current ?? 0) + delta;
  return Math.min(5, Math.max(0.5, Math.round(next * 2) / 2));
}

/** Remplissage d'une étoile (0, 0,5 ou 1) pour une note donnée. */
export function starFill(star: number, score: number | null): 0 | 0.5 | 1 {
  if (score === null) return 0;
  if (score >= star) return 1;
  if (score >= star - 0.5) return 0.5;
  return 0;
}

/** « 4,5 » : note à la française. */
export const scoreLabel = (score: number) => score.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

/** Mot associé à la note générale, façon livraison de repas. */
export function scoreWord(score: number | null): string {
  if (score === null) return 'Touchez les étoiles pour noter';
  if (score <= 1.5) return 'Décevante';
  if (score <= 2.5) return 'Moyenne';
  if (score <= 3.5) return 'Correcte';
  if (score <= 4.5) return 'Très bien';
  return 'Excellente';
}

/** Envoi possible : la note générale est obligatoire, le commentaire borné. */
export function ratingReady(draft: RatingDraft): boolean {
  return isStarScore(draft.score) && draft.comment.trim().length <= RATING_COMMENT_MAX;
}

/** Corps envoyé : critères notés seulement, commentaire vide omis. */
export function ratingPayload(draft: RatingDraft): {
  score: number;
  criteria?: Record<string, number>;
  comment?: string;
} {
  const criteria = Object.fromEntries(Object.entries(draft.criteria).filter(([, v]) => isStarScore(v)));
  const comment = draft.comment.trim();
  return {
    score: draft.score as number,
    ...(Object.keys(criteria).length ? { criteria } : {}),
    ...(comment ? { comment } : {}),
  };
}

/** Clé de session : la fenêtre ne s'ouvre d'elle-même qu'une fois par session et par élève. */
export const ratingPromptKey = (candidateId: string) => `pmd-candidat-rating-prompted:${candidateId}`;
